/**
* @name: ExportSchemeModal
* @description: 导出方案配置弹窗
*                - 左侧：已配置方案名称列表（含删除），弹窗打开/新增/修改/删除后均从接口重新加载
*                - 右侧：当前选中方案详情（方案名称 + 字段配置表格 + 保存）
*                - 仅方案创建人可变更名称/全局应用/导出字段及保存、同步、删除；他人方案只读并展示创建人
*                - 新增方案时，右侧字段列表通过 getComponentByPageCode 接口实时获取
*                - 方案的新增/修改/删除均对接 /api/go-lcc/pageExportConfig/* 接口，不再使用 mock 数据
*/
<template>
  <a-modal
    :visible="props.visible"
    title="配置导出方案"
    :width="'85%'"
    :footer="null"
    :centered="true"
    :mask-closable="false"
    :keyboard="true"
    :destroy-on-close="true"
    :get-container="container"
    :bodyStyle="modalBodyStyle"
    class="export-scheme-modal"
    @cancel="onModalCancel"
  >
    <div class="scheme-toolbar">
      <div class="scheme-usage-tip">
        <InfoCircleOutlined class="scheme-usage-tip-icon" />
        <span class="scheme-usage-tip-text">提示：方案保存后，可在「导出」菜单中悬停「导出所有/导出选中」选择方案进行导出</span>
      </div>
      <div class="scheme-toolbar-actions">
        <a-popover
          v-model:visible="toolbarAddPopoverVisible"
          trigger="click"
          placement="bottomRight"
          overlay-class-name="add-scheme-popover"
        >
          <a-button size="small" type="primary" class="scheme-toolbar-add-btn" @click="onAddBtnClick('toolbar')">
            <template #icon><PlusOutlined/></template>
            新增
          </a-button>
          <template #content>
            <div class="add-scheme-popover-body">
              <div class="add-scheme-popover-label">方案名称</div>
              <a-input
                ref="toolbarAddInputRef"
                v-model:value="newSchemeName"
                placeholder="请输入方案名称"
                :class="{ 'ant-input-error': !!newSchemeNameError }"
                :disabled="addingScheme"
                @input="newSchemeNameError = ''"
                @pressEnter="onConfirmAddScheme"
              />
              <div v-if="newSchemeNameError" class="add-scheme-popover-error">{{ newSchemeNameError }}</div>
              <div class="add-scheme-popover-actions">
                <a-button size="small" :disabled="addingScheme" @click="closeAddPopover">取消</a-button>
                <a-button size="small" type="primary" :loading="addingScheme" @click="onConfirmAddScheme">确定</a-button>
              </div>
            </div>
          </template>
        </a-popover>
      </div>
    </div>

    <div class="scheme-modal-body">
      <!-- 无方案：整块内容区居中展示单一空状态 -->
      <div v-if="!schemeListLoading && schemeList.length === 0" class="scheme-body-empty">
        <FileTextOutlined class="scheme-empty-icon scheme-empty-icon--lg"/>
        <p class="scheme-empty-text">暂无方案，请点击新增创建</p>
        <a-popover
          v-model:visible="emptyAddPopoverVisible"
          trigger="click"
          placement="bottom"
          overlay-class-name="add-scheme-popover"
        >
          <a-button type="primary" class="scheme-empty-add-btn" @click="onAddBtnClick('empty')">
            <template #icon><PlusOutlined/></template>
            新增
          </a-button>
          <template #content>
            <div class="add-scheme-popover-body">
              <div class="add-scheme-popover-label">方案名称</div>
              <a-input
                ref="emptyAddInputRef"
                v-model:value="newSchemeName"
                placeholder="请输入方案名称"
                :class="{ 'ant-input-error': !!newSchemeNameError }"
                :disabled="addingScheme"
                @input="newSchemeNameError = ''"
                @pressEnter="onConfirmAddScheme"
              />
              <div v-if="newSchemeNameError" class="add-scheme-popover-error">{{ newSchemeNameError }}</div>
              <div class="add-scheme-popover-actions">
                <a-button size="small" :disabled="addingScheme" @click="closeAddPopover">取消</a-button>
                <a-button size="small" type="primary" :loading="addingScheme" @click="onConfirmAddScheme">确定</a-button>
              </div>
            </div>
          </template>
        </a-popover>
      </div>
      <template v-else>
        <!-- 左侧：方案名称列表（外层 div 固定栏宽，避免 a-spin 撑破） -->
        <div class="scheme-left">
          <a-spin :spinning="schemeListLoading" class="scheme-left-spin">
            <div
              v-for="s in schemeList"
              :key="s.id"
              class="scheme-left-item"
              :class="{ 'is-active': s.id === activeSchemeId }"
              @click="selectScheme(s.id)"
            >
              <span class="scheme-left-name" :title="s.name">{{ s.name }}</span>
              <a-popconfirm
                v-if="canMutateScheme(s)"
                title="确定删除该方案吗？"
                ok-text="删除"
                cancel-text="取消"
                @confirm="deleteScheme(s.id)"
              >
                <span
                  class="scheme-delete-icon remix-icon ri-delete-bin-line"
                  :class="{ 'is-loading': deletingSchemeId === s.id }"
                  @click.stop
                ></span>
              </a-popconfirm>
            </div>
          </a-spin>
        </div>

        <!-- 右侧：方案详情 -->
        <div class="scheme-right">
          <template v-if="activeScheme">
          <div class="scheme-right-header">
            <a-row :gutter="12" align="middle" class="scheme-name-row">
              <a-col :span="6" class="scheme-header-col">
                <span class="scheme-name-label">方案名称：</span>
                <a-input
                  v-model:value="editingName"
                  class="scheme-name-input"
                  :class="{ 'is-readonly': isOthersSchemeReadonly }"
                  :placeholder="isOthersSchemeReadonly ? '' : '请输入方案名称'"
                  :readonly="isOthersSchemeReadonly"
                  :bordered="!isOthersSchemeReadonly"
                />
              </a-col>
              <a-col :span="6" class="scheme-header-col">
                <span class="scheme-name-label">全局应用：</span>
                <a-switch
                  :checked="editingGlobalApply"
                  size="small"
                  :class="{ 'is-readonly': isOthersSchemeReadonly }"
                  @update:checked="onEditingGlobalApplyChange"
                />
                <a-tooltip title="关闭时该方案仅创建者自己可见/可用；开启后该方案对所有人可见/可用">
                  <QuestionCircleOutlined class="scheme-header-help-tip"/>
                </a-tooltip>
              </a-col>
              <a-col v-if="isOthersSchemeReadonly" :span="12" class="scheme-header-col scheme-header-col--creator">
                <span class="scheme-name-label">创建人：</span>
                <div class="scheme-creator-ctl readOnlyText UserSelect">
                  <select-user-ctl
                    :value="creatorDisplayValue"
                    :disabled="true"
                    :read-only-text="true"
                    :has-del="false"
                    :multiple="false"
                    :item="creatorSelectItem"
                  />
                </div>
                <a-tooltip title="非本人创建的方案不可修改">
                  <QuestionCircleOutlined class="scheme-header-help-tip"/>
                </a-tooltip>
              </a-col>
            </a-row>
            <a-tooltip
              v-if="!activeScheme.isDraft && canEditActiveScheme"
              title="从设计器拉取最新可配置字段，保留当前方案的导出勾选并自动保存"
            >
              <a-button size="small" :loading="syncing" :disabled="saving" @click="onSync">同步</a-button>
            </a-tooltip>
          </div>

          <div class="scheme-fields-table">
            <div class="fields-row fields-header">
              <span class="col-index">序号</span>
              <span class="col-name">字段名称</span>
              <span class="col-check">
                <a-checkbox
                  :checked="allFieldsChecked"
                  :indeterminate="fieldsCheckIndeterminate"
                  :class="{ 'is-readonly': isOthersSchemeReadonly }"
                  @change="onToggleAllFields"
                />
                <span class="col-check-label">是否导出</span>
              </span>
              <span class="col-code">字段编码</span>
            </div>
            <div class="fields-body">
              <div v-for="(f, idx) in editingFields" :key="`${getSchemeFieldIdentity(f)}#${idx}`" class="fields-row">
                <span class="col-index">{{ idx + 1 }}</span>
                <span class="col-name" :title="getFieldDisplayName(f)">{{ getFieldDisplayName(f) }}</span>
                <span class="col-check">
                  <a-checkbox
                    :checked="f.inTemplate"
                    :class="{ 'is-readonly': isOthersSchemeReadonly }"
                    @update:checked="(checked) => setFieldExport(f, checked)"
                  />
                </span>
                <span class="col-code">{{ f.code }}</span>
              </div>
            </div>
          </div>

          <div v-if="canEditActiveScheme" class="scheme-save-row">
            <a-button type="primary" :loading="saving" :disabled="syncing" @click="onSave">保存</a-button>
          </div>
        </template>
        <div v-else class="scheme-right-empty">请选择左侧方案</div>
        </div>
      </template>
    </div>
  </a-modal>
</template>

<script lang="ts">
import {defineComponent} from 'vue';

export default defineComponent({
  components: {},
  name: "ExportSchemeModal",
});
</script>

<script lang="ts" setup>
import {ref, computed, watch, nextTick, h} from 'vue';
import {
  Modal as AModal,
  Modal,
  Button as AButton,
  Input as AInput,
  Checkbox as ACheckbox,
  Switch as ASwitch,
  Popconfirm as APopconfirm,
  Popover as APopover,
  Tooltip as ATooltip,
  Spin as ASpin,
  Row as ARow,
  Col as ACol,
  message,
} from 'ant-design-vue';
import {PlusOutlined, QuestionCircleOutlined, FileTextOutlined, InfoCircleOutlined} from '@ant-design/icons-vue';
import {StorageUtils} from 'xmgoing-utils';
import GoingUtils from '@/utils/goingUtils';
import SelectUserCtl from '@/components/selectUserCtl/index.vue';
import {getComponentByPageCode} from '@/main-page/servers/formPageServer';
import {
  getPageExportConfigByPageCode,
  savePageExportConfig,
  updatePageExportConfig,
  removePageExportConfig,
} from '../server/pageExportConfig';
import {type SchemeField, type SchemeItem, getSchemeFieldIdentity} from './exportSchemeTypes';

const container = computed(() => {
  if (GoingUtils.getIsPreview()) return false;
  return document.querySelector('.center-aside') || document.querySelector('#__qiankun_microapp_wrapper_for_xm_erp__');
});

/** 弹窗整体约视口 80%，内容区扣除标题栏；写法对齐高级查询弹窗，避免高度随内容收缩 */
const modalBodyStyle = computed(() => {
  const viewportH = window.innerHeight;
  const modalH = viewportH * 0.8;
  const headerH = 55;
  return {
    height: `${modalH - headerH}px`,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };
});

interface PropTypes {
  visible: boolean
  /** 获取可导出组件列接口的请求参数：pageCode / mainKey / subKey / bjModuleCode / bjPageCode */
  exportColumnParams?: Record<string, any>
}

const props = withDefaults(defineProps<PropTypes>(), {
  visible: false,
  exportColumnParams: () => ({}),
});

const emit = defineEmits<{
  (e: 'update:visible', val: boolean): void
  /** 方案增删改后通知父组件更新导出下拉菜单缓存 */
  (e: 'menu-schemes-change', options: { id: string; name: string }[]): void
}>();

/** 方案列表，弹窗打开时从接口加载 */
const schemeList = ref<SchemeItem[]>([]);
const activeSchemeId = ref('');
const schemeListLoading = ref(false);

const activeScheme = computed(() => schemeList.value.find((s) => s.id === activeSchemeId.value) || null);

/** 右侧编辑态数据，保存前不直接改动 schemeList */
const editingName = ref('');
const editingGlobalApply = ref(false);
const editingFields = ref<SchemeField[]>([]);
/** 进入编辑态时的快照，用于判断是否有未保存修改 */
const editingSnapshot = ref<{
  name: string;
  globalApply: boolean;
  fields: SchemeField[];
} | null>(null);

const cloneFields = (fields: SchemeField[]) => fields.map((f) => ({...f}));

const isSameSchemeFields = (a: SchemeField[], b: SchemeField[]) => {
  if (a.length !== b.length) return false;
  const inTemplateById = new Map(b.map((f) => [getSchemeFieldIdentity(f), f.inTemplate]));
  return a.every((f) => inTemplateById.get(getSchemeFieldIdentity(f)) === f.inTemplate);
};

const isEditingDirty = computed(() => {
  if (!activeScheme.value || !editingSnapshot.value) return false;
  const snap = editingSnapshot.value;
  if (editingName.value.trim() !== snap.name.trim()) return true;
  if (editingGlobalApply.value !== snap.globalApply) return true;
  return !isSameSchemeFields(editingFields.value, snap.fields);
});

/** 离开当前方案/关窗前是否需要确认：可变更且（有编辑差异或仍是未落库草稿） */
const needsLeaveConfirm = computed(() =>
  canEditActiveScheme.value && (isEditingDirty.value || !!activeScheme.value?.isDraft),
);

/** 用当前编辑态重写快照，保存成功后显式清掉未保存修改 */
const syncEditingSnapshotFromEditing = () => {
  editingSnapshot.value = {
    name: editingName.value,
    globalApply: editingGlobalApply.value,
    fields: cloneFields(editingFields.value),
  };
};

const allFieldsChecked = computed(() =>
  editingFields.value.length > 0 && editingFields.value.every((f) => f.inTemplate),
);

const fieldsCheckIndeterminate = computed(() => {
  const checkedCount = editingFields.value.filter((f) => f.inTemplate).length;
  return checkedCount > 0 && checkedCount < editingFields.value.length;
});

const onToggleAllFields = (e: any) => {
  if (!canEditActiveScheme.value) return;
  const checked = typeof e === 'boolean' ? e : !!e?.target?.checked;
  editingFields.value.forEach((f) => {
    f.inTemplate = checked;
  });
};

const onEditingGlobalApplyChange = (checked: boolean) => {
  if (!canEditActiveScheme.value) return;
  editingGlobalApply.value = checked;
};

const setFieldExport = (field: SchemeField, checked: boolean) => {
  if (!canEditActiveScheme.value) return;
  field.inTemplate = checked;
};

const getCurrentUserId = () =>
  String(StorageUtils.getSessionItem('curUserId') || StorageUtils.getSessionItem('userId') || '');

const normalizeCreateUser = (item: any): string =>
  String(item?.createUser ?? item?.create_user ?? '').trim();

/** 创建人可变更：草稿、无创建人旧方案、创建人等于当前登录用户 */
const canMutateScheme = (scheme: SchemeItem | null | undefined): boolean => {
  if (!scheme) return false;
  if (scheme.isDraft) return true;
  const creatorId = scheme.createUser;
  if (!creatorId) return true;
  const curUserId = getCurrentUserId();
  return !!curUserId && creatorId === curUserId;
};

const canEditActiveScheme = computed(() => canMutateScheme(activeScheme.value));
const isOthersSchemeReadonly = computed(() =>
  !!activeScheme.value && !canEditActiveScheme.value,
);
const creatorDisplayValue = computed(() => {
  const scheme = activeScheme.value;
  if (!scheme) return '';
  if (scheme.createUser) return scheme.createUser;
  if (scheme.isDraft) return getCurrentUserId();
  return '';
});
const creatorSelectItem = {title: '创建人', showSelIcon: false};

watch(activeScheme, (scheme) => {
  if (scheme) {
    editingName.value = scheme.name;
    editingGlobalApply.value = scheme.globalApply;
    editingFields.value = cloneFields(scheme.fields);
    editingSnapshot.value = {
      name: scheme.name,
      globalApply: scheme.globalApply,
      fields: cloneFields(scheme.fields),
    };
  } else {
    editingName.value = '';
    editingGlobalApply.value = false;
    editingFields.value = [];
    editingSnapshot.value = null;
  }
}, {immediate: true});

/** 不可作为导出字段的组件类型：序号列、操作栏 */
const EXCLUDED_COMPONENT_TYPES = ['orderNum', 'operation'];

const isExportableColumn = (item: any) =>
  !EXCLUDED_COMPONENT_TYPES.includes(item?.componentType);

/** 子表字段在列表中的展示名：source=sub 时在 name 前加【sourceName】 */
const getFieldDisplayName = (field: SchemeField): string => {
  const name = field.name ?? '';
  if (field.source === 'sub' && field.sourceName) {
    return `【${field.sourceName}】${name}`;
  }
  return name;
};

/** 归一化单条字段：保留接口原始字段（componentType/config/source/sourceName 等），仅补齐 code/name/inTemplate */
const normalizeInTemplate = (f: any): boolean => {
  const raw = f?.inTemplate ?? f?.isTemplate ?? f?.checked ?? f?.exportFlag;
  if (raw === undefined || raw === null) return true;
  return raw === true || raw === 'true' || raw === 1 || raw === '1';
};

const normalizeColumnItem = (f: any): SchemeField => ({
  ...f,
  code: f?.code ?? f?.fieldCode ?? f?.columnName ?? '',
  name: f?.name ?? f?.fieldName ?? f?.columnCaption ?? '',
  inTemplate: normalizeInTemplate(f),
});

/** 归一化单条方案记录：接口字段为 pageCode + configName（方案名称）+ columns（字段配置数组） */
const normalizeSchemeRecord = (item: any): SchemeItem | null => {
  if (!item) return null;
  const id = item.id ?? '';
  if (!id) return null;
  const rawColumns = item.columns ?? [];
  let parsedColumns: any = rawColumns;
  if (typeof rawColumns === 'string') {
    try {
      parsedColumns = JSON.parse(rawColumns || '[]');
    } catch (e) {
      console.error('解析导出方案字段配置失败:', e);
      parsedColumns = [];
    }
  }
  const fields: SchemeField[] = Array.isArray(parsedColumns)
    ? parsedColumns
      .filter(isExportableColumn)
      .map(normalizeColumnItem)
      .filter((f) => !!f.code)
    : [];
  return {
    id: String(id),
    name: item.configName ?? '',
    // 全局应用：后端接口暂未声明该字段，先按 globalApply 读取，等待后端配合支持
    globalApply: item.globalApply === true || item.globalApply === 'true' || item.globalApply === 1,
    createUser: normalizeCreateUser(item),
    fields,
  };
};

/** 从 save/update 响应中取出方案详情（兼容 { data: {...} } 与直接返回） */
const extractSchemeDetailPayload = (res: any): any => {
  if (!res) return null;
  const detail = res.data ?? res;
  if (detail?.data && (detail.data.id != null || detail.data.configName != null)) {
    return detail.data;
  }
  if (detail?.id != null || detail?.configName != null) {
    return detail;
  }
  return null;
};

/** 用 save/update 返回的方案详情更新本地列表，不重新拉取列表（避免本地草稿被覆盖） */
const applySavedSchemeFromResponse = (res: any, previousId: string): boolean => {
  const saved = normalizeSchemeRecord(extractSchemeDetailPayload(res));
  if (!saved) return false;
  const idx = schemeList.value.findIndex((s) => s.id === previousId);
  if (idx >= 0) {
    const prev = schemeList.value[idx];
    schemeList.value[idx] = {
      ...saved,
      createUser: saved.createUser || prev.createUser || '',
    };
  } else {
    schemeList.value.push(saved);
  }
  activeSchemeId.value = saved.id;
  notifyMenuSchemesChange();
  return true;
};

/** 从接口加载方案列表，尽量保留当前选中项；getByPageCode 仅按 pageCode 查询 */
const loadSchemeList = (
  selectId?: string,
  options?: { notifyMenu?: boolean; onLoaded?: () => void },
) => {
  schemeListLoading.value = true;
  getPageExportConfigByPageCode({
    pageCode: props.exportColumnParams?.pageCode,
    backFun: (res: any) => {
      schemeListLoading.value = false;
      const list: SchemeItem[] = Array.isArray(res)
        ? res.map(normalizeSchemeRecord).filter((s): s is SchemeItem => !!s)
        : [];
      schemeList.value = list;
      const keepId = selectId || activeSchemeId.value;
      activeSchemeId.value = list.find((s) => s.id === keepId)?.id || list[0]?.id || '';
      if (options?.notifyMenu) {
        notifyMenuSchemesChange();
      }
      options?.onLoaded?.();
    },
    errorFun: () => {
      schemeListLoading.value = false;
      message.error('获取导出方案列表失败，请稍后重试');
    },
  });
};

/** 通知父组件刷新导出下拉菜单的方案缓存（仅已落库方案） */
const notifyMenuSchemesChange = () => {
  emit('menu-schemes-change', schemeList.value
    .filter((s) => !s.isDraft && !!s.name)
    .map((s) => ({ id: s.id, name: s.name })));
};

watch(() => props.visible, (val) => {
  if (val) {
    // 打开时同步刷新导出下拉的方案缓存，避免空缓存导致无子菜单箭头
    loadSchemeList(undefined, { notifyMenu: true });
  }
});

const applySchemeSwitch = (id: string) => {
  activeSchemeId.value = id;
};

type LeaveMode = 'switch' | 'close';

/** 离开当前方案前：有未保存修改或未落库草稿时提示保存/放弃/取消 */
const tryLeaveCurrentScheme = (onLeave: () => void, mode: LeaveMode = 'switch') => {
  if (!needsLeaveConfirm.value) {
    onLeave();
    return;
  }
  const actionWord = mode === 'close' ? '关闭' : '切换';
  Modal.confirm({
    title: '提示',
    content: h('div', [
      h('p', { style: { marginBottom: '8px' } }, `当前方案有未保存的修改，是否先保存再${actionWord}？`),
      h('a', {
        class: 'scheme-switch-discard',
        onClick: (e: Event) => {
          e.preventDefault();
          Modal.destroyAll();
          onLeave();
        },
      }, `不保存，直接${actionWord}`),
    ]),
    okText: `保存并${actionWord}`,
    cancelText: '取消',
    onOk: () => new Promise((resolve, reject) => {
      if (!validateSchemeName()) {
        reject(new Error('invalid'));
        return;
      }
      saving.value = true;
      persistActiveScheme(
        editingFields.value,
        '保存成功',
        () => {
          onLeave();
          resolve(true);
        },
        () => reject(new Error('save failed')),
      );
    }),
  });
};

/** 关闭弹窗（X / ESC）：与切方案同一套离开确认 */
const onModalCancel = () => {
  tryLeaveCurrentScheme(() => emit('update:visible', false), 'close');
};

const selectScheme = (id: string) => {
  if (activeSchemeId.value === id) return;
  tryLeaveCurrentScheme(() => applySchemeSwitch(id), 'switch');
};

/** 新增方案悬浮框：右上角与空态各挂一份，互斥显隐，共用输入态 */
type AddPopoverSource = 'toolbar' | 'empty';
const toolbarAddPopoverVisible = ref(false);
const emptyAddPopoverVisible = ref(false);
const newSchemeName = ref('');
const newSchemeNameError = ref('');
const toolbarAddInputRef = ref<InstanceType<typeof AInput> | null>(null);
const emptyAddInputRef = ref<InstanceType<typeof AInput> | null>(null);
/** 新增方案时拉取可配置字段的加载态，避免重复提交 */
const addingScheme = ref(false);

const closeAddPopover = () => {
  toolbarAddPopoverVisible.value = false;
  emptyAddPopoverVisible.value = false;
};

/** 方案名称是否与其他方案重复（编辑时可排除当前方案） */
const isDuplicateSchemeName = (name: string, excludeId?: string) =>
  schemeList.value.some((s) => s.name === name && s.id !== excludeId);

/**
 * 点击「新增」时确保对应入口的悬浮框打开；已打开时不做处理，关闭交给 a-popover 自身的切换逻辑，
 * 这样无论两个 click 处理谁先执行，结果都一致。空态入口在按钮旁弹出，避免跳到右上角。
 */
const onAddBtnClick = (source: AddPopoverSource) => {
  if (source === 'toolbar') {
    emptyAddPopoverVisible.value = false;
    if (!toolbarAddPopoverVisible.value) {
      toolbarAddPopoverVisible.value = true;
    }
    return;
  }
  toolbarAddPopoverVisible.value = false;
  if (!emptyAddPopoverVisible.value) {
    emptyAddPopoverVisible.value = true;
  }
};

const resetAddPopoverForm = () => {
  newSchemeName.value = '';
  newSchemeNameError.value = '';
};

/** 悬浮框打开时重置输入态并聚焦对应输入框 */
watch(toolbarAddPopoverVisible, (visible) => {
  if (!visible) return;
  resetAddPopoverForm();
  nextTick(() => {
    toolbarAddInputRef.value?.focus();
  });
});
watch(emptyAddPopoverVisible, (visible) => {
  if (!visible) return;
  resetAddPopoverForm();
  nextTick(() => {
    emptyAddInputRef.value?.focus();
  });
});

/** 归一化 getComponentByPageCode 返回的组件列表，过滤序号/操作栏列 */
const normalizeColumnFields = (list: any): SchemeField[] => {
  if (!Array.isArray(list)) return [];
  return list
    .filter(isExportableColumn)
    .map(normalizeColumnItem)
    .filter((f) => !!f.code);
};

/** 用设计器最新字段覆盖方案列清单，按字段身份保留原方案的 inTemplate 勾选 */
const mergeFieldsWithLatest = (latestFields: SchemeField[], currentFields: SchemeField[]): SchemeField[] => {
  const inTemplateById = new Map(currentFields.map((f) => [getSchemeFieldIdentity(f), f.inTemplate]));
  return latestFields.map((f) => ({
    ...f,
    inTemplate: inTemplateById.get(getSchemeFieldIdentity(f)) ?? false,
  }));
};

const createDraftScheme = (trimmed: string) => {
  addingScheme.value = true;
  getComponentByPageCode({
    ...props.exportColumnParams,
    backFun: (res: any) => {
      addingScheme.value = false;
      const fields = normalizeColumnFields(res);
      if (fields.length === 0) {
        message.warning('未获取到可配置的导出字段');
        return;
      }
      const id = `draft_${Date.now()}`;
      const newScheme: SchemeItem = {
        id,
        name: trimmed,
        globalApply: false,
        createUser: getCurrentUserId(),
        fields,
        isDraft: true,
      };
      schemeList.value.push(newScheme);
      activeSchemeId.value = id;
      closeAddPopover();
    },
    errorFun: () => {
      addingScheme.value = false;
      message.error('获取可配置的导出字段失败，请稍后重试');
    },
  });
};

const onConfirmAddScheme = () => {
  if (addingScheme.value) return;
  const trimmed = newSchemeName.value.trim();
  if (!trimmed) {
    newSchemeNameError.value = '请输入方案名称';
    return;
  }
  if (isDuplicateSchemeName(trimmed)) {
    newSchemeNameError.value = '方案名称已存在';
    return;
  }
  tryLeaveCurrentScheme(() => createDraftScheme(trimmed), 'switch');
};

/** 删除中的方案 id，避免重复提交 */
const deletingSchemeId = ref('');

const deleteScheme = (id: string) => {
  if (deletingSchemeId.value) return;
  const scheme = schemeList.value.find((s) => s.id === id);
  if (!scheme || !canMutateScheme(scheme)) return;
  /** 本地草稿未落库，直接从列表移除 */
  if (scheme.isDraft) {
    const idx = schemeList.value.findIndex((s) => s.id === id);
    if (idx === -1) return;
    schemeList.value.splice(idx, 1);
    if (activeSchemeId.value === id) {
      activeSchemeId.value = schemeList.value[0]?.id || '';
    }
    return;
  }
  deletingSchemeId.value = id;
  removePageExportConfig(
    id,
    () => {
      deletingSchemeId.value = '';
      message.success('删除成功');
      const idx = schemeList.value.findIndex((s) => s.id === id);
      if (idx >= 0) {
        schemeList.value.splice(idx, 1);
      }
      if (activeSchemeId.value === id) {
        activeSchemeId.value = schemeList.value[0]?.id || '';
      }
      notifyMenuSchemesChange();
    },
    () => {
      deletingSchemeId.value = '';
      message.error('删除方案失败，请稍后重试');
    },
  );
};

const saving = ref(false);
const syncing = ref(false);

/** 校验方案名称并组装 save/update 公共 payload */
const buildSchemePayload = (fields: SchemeField[]) => ({
  pageCode: props.exportColumnParams?.pageCode,
  configName: editingName.value.trim(),
  columns: cloneFields(fields),
  globalApply: editingGlobalApply.value,
});

/** 落库当前选中方案（新增草稿走 save，已保存走 update） */
const persistActiveScheme = (
  fields: SchemeField[],
  successMsg: string,
  onDone?: () => void,
  onFail?: () => void,
) => {
  const scheme = activeScheme.value;
  if (!scheme || !canMutateScheme(scheme)) {
    saving.value = false;
    syncing.value = false;
    onFail?.();
    return;
  }
  const payload = buildSchemePayload(fields);
  const previousId = scheme.id;
  const onSaved = (res: any) => {
    saving.value = false;
    syncing.value = false;
    if (applySavedSchemeFromResponse(res, previousId)) {
      // 保存成功后显式对齐快照，避免仅依赖列表替换 + watch 导致仍判定为未保存
      syncEditingSnapshotFromEditing();
      message.success(successMsg);
      onDone?.();
      return;
    }
    message.error('保存成功，但解析方案详情失败');
    onFail?.();
  };
  const onSaveError = () => {
    saving.value = false;
    syncing.value = false;
    message.error('保存失败，请稍后重试');
    onFail?.();
  };
  if (scheme.isDraft) {
    savePageExportConfig(
      payload,
      onSaved,
      onSaveError,
    );
    return;
  }
  updatePageExportConfig(
    {
      id: scheme.id,
      ...payload,
    },
    onSaved,
    onSaveError,
  );
};

/** 保存前校验方案名称 */
const validateSchemeName = (): boolean => {
  if (!activeScheme.value) return false;
  const trimmed = editingName.value.trim();
  if (!trimmed) {
    message.warning('请输入方案名称');
    return false;
  }
  if (isDuplicateSchemeName(trimmed, activeScheme.value.id)) {
    message.warning('方案名称已存在');
    return false;
  }
  return true;
};

const onSave = () => {
  if (!activeScheme.value || saving.value || syncing.value || !canEditActiveScheme.value) return;
  if (!validateSchemeName()) return;
  saving.value = true;
  persistActiveScheme(editingFields.value, '保存成功');
};

/** 从设计器同步最新可配置字段，保留导出勾选并自动保存 */
const onSync = () => {
  if (!activeScheme.value || syncing.value || saving.value || !canEditActiveScheme.value) return;
  if (!validateSchemeName()) return;
  syncing.value = true;
  getComponentByPageCode({
    ...props.exportColumnParams,
    backFun: (res: any) => {
      const latestFields = normalizeColumnFields(res);
      if (latestFields.length === 0) {
        syncing.value = false;
        message.warning('未获取到可配置的导出字段');
        return;
      }
      const mergedFields = mergeFieldsWithLatest(latestFields, editingFields.value);
      editingFields.value = mergedFields;
      persistActiveScheme(mergedFields, '同步成功');
    },
    errorFun: () => {
      syncing.value = false;
      message.error('获取最新可配置字段失败，请稍后重试');
    },
  });
};
</script>

<style type="text/css" lang="less">
.add-scheme-popover {
  .ant-popover-inner-content {
    padding: 12px;
  }
}
.add-scheme-popover-body {
  width: 220px;

  .add-scheme-popover-label {
    margin-bottom: 6px;
    font-size: 13px;
    color: #333;
  }
}
.add-scheme-popover-error {
  margin-top: 4px;
  font-size: 12px;
  color: #ff4d4f;
}
.add-scheme-popover-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
}

.export-scheme-modal {
  .ant-modal-body {
    padding: 12px 24px 24px;
  }

  .scheme-switch-discard {
    font-size: 13px;
  }

  .scheme-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-shrink: 0;
    margin-bottom: 12px;
  }
  .scheme-usage-tip {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 6px;
    line-height: 20px;
  }
  .scheme-usage-tip-icon {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 20px;
    font-size: 14px;
    color: #8c8c8c;
    line-height: 0;

    > svg {
      display: block;
      width: 14px;
      height: 14px;
    }
  }
  .scheme-usage-tip-text {
    font-size: 13px;
    line-height: 20px;
    color: #8c8c8c;
  }
  .scheme-toolbar-actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }
  .scheme-toolbar-add-btn {
    margin: 0;
  }

  .scheme-modal-body {
    display: flex;
    flex: 1;
    min-height: 0;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    overflow: hidden;
    position: relative;
  }

  .scheme-body-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    width: 100%;
    min-height: 0;
    text-align: center;
  }

  .scheme-left {
    flex: 0 0 200px;
    width: 200px;
    max-width: 200px;
    min-width: 200px;
    border-right: 1px solid #e8e8e8;
    overflow-x: hidden;
    overflow-y: auto;
    box-sizing: border-box;
  }
  .scheme-left-spin {
    width: 100%;

    .ant-spin-container {
      width: 100%;
      overflow: hidden;
    }
  }
  .scheme-empty-icon {
    font-size: 36px;
    color: #d9d9d9;
    margin-bottom: 10px;

    &--lg {
      font-size: 56px;
      margin-bottom: 12px;
    }
  }
  .scheme-empty-text {
    margin: 0;
    color: #999;
    font-size: 13px;
    line-height: 1.6;
  }
  .scheme-empty-add-btn {
    margin-top: 16px;
  }
  .scheme-left-item {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    column-gap: 6px;
    padding: 9px 12px;
    font-size: 13px;
    color: #333;
    cursor: pointer;
    transition: background 0.15s;
    width: 100%;
    box-sizing: border-box;
    overflow: hidden;

    &:hover {
      background: #f5f7fa;

      .scheme-delete-icon {
        opacity: 1;
      }
    }
    &.is-active {
      color: #1890ff;
      background: #e6f7ff;
    }
  }
  .scheme-left-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .scheme-delete-icon {
    flex-shrink: 0;
    color: #bbb;
    font-size: 14px;
    opacity: 0;
    transition: opacity 0.15s, color 0.15s;

    &:hover {
      color: #f5222d;
    }
    &.is-loading {
      opacity: 1;
      color: #ccc;
      pointer-events: none;
    }
  }

  .scheme-right {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    padding: 12px 16px;
  }
  .scheme-right-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #bbb;
    font-size: 13px;
  }
  .scheme-right-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
    flex-shrink: 0;
  }
  .scheme-name-row {
    flex: 1;
    min-width: 0;
    margin-bottom: 0;

    .scheme-header-col {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
    }
    .scheme-header-col--creator {
      flex-wrap: nowrap;
      justify-content: flex-start;

      .scheme-creator-ctl {
        flex: 0 0 auto;
        width: auto;
        max-width: 50%;
      }
      .scheme-header-help-tip {
        margin-left: 0;
      }
    }
    .scheme-name-label {
      flex-shrink: 0;
      font-size: 13px;
      color: #333;
    }
    .scheme-header-help-tip {
      flex-shrink: 0;
      color: #bbb;
      font-size: 13px;
      cursor: help;

      &:hover {
        color: #1890ff;
      }
    }
    .scheme-name-input {
      flex: 1;
      min-width: 0;

      &.is-readonly,
      &.is-readonly.ant-input,
      &.is-readonly .ant-input {
        background: transparent !important;
        border: 0 !important;
        box-shadow: none !important;
        color: #333;
        cursor: default;
        padding-left: 0;
      }
    }
    .ant-switch.is-readonly {
      cursor: default;
      pointer-events: none;
    }
  }
  .scheme-creator-ctl {
    width: auto;
    max-width: 200px;
    flex: 0 0 auto;

    .selectUser-sty {
      width: auto;
      flex: none;
    }
    .index-module__select-wrap--EGt10 {
      flex: none;
      width: auto;
    }
    .ant-select {
      width: auto !important;
    }
    .ant-select-selector {
      width: auto !important;
      padding: 0 !important;
      height: auto !important;
    }
    .ant-select-selection-overflow {
      flex-wrap: nowrap;
    }

    &.readOnlyText {
      .sel-icon {
        display: none;
      }
      .ant-select-multiple .ant-select-selection-item {
        background-color: transparent;
        border: 0;
        margin: 0;
        padding-left: 0;
      }
      .ant-select-selection-item-remove {
        display: none;
      }
      .ant-select-selection-item-content {
        color: #333 !important;
      }
      .ant-select-disabled.ant-select:not(.ant-select-customize-input) .ant-select-selector {
        background-color: transparent !important;
        border: 0;
        box-shadow: none;
        cursor: default;
        padding-left: 0;
      }
    }
  }

  .scheme-fields-table {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    border: 1px solid #f0f0f0;
    border-radius: 4px;
    overflow: hidden;
  }
  .fields-row {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    font-size: 13px;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }

    .col-index {
      width: 50px;
      flex-shrink: 0;
      color: #999;
    }
    .col-name {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
      .col-check {
      width: 100px;
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      gap: 6px;

      .col-check-label {
        font-size: 13px;
        color: inherit;
        font-weight: inherit;
      }
      .ant-checkbox-wrapper.is-readonly {
        cursor: default;
        pointer-events: none;
      }
    }
    .col-code {
      flex: 1;
      min-width: 0;
      color: #999;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  .fields-header {
    flex-shrink: 0;
    background: #fafafa;
    color: #666;
    font-weight: 500;
  }
  .fields-body {
    flex: 1;
    overflow-y: auto;
  }

  .scheme-save-row {
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
  }
}
</style>
